/**
 * DataTable Component
 * 
 * Componente de tabela reutilizável para exibição de dados.
 * Suporta paginação, ordenação, filtros e ações customizadas.
 * 
 * Funcionalidades:
 * - Exibição de dados em formato tabular
 * - Paginação automática
 * - Ordenação por colunas
 * - Ações por linha (editar, excluir, visualizar)
 * - Loading state
 * - Empty state
 */

import { useState } from 'react'
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import './DataTable.css'

function DataTable({
  columns = [],
  data = [],
  loading = false,
  onRowClick,
  actions = [],
  pagination = null,
  emptyMessage = 'Nenhum registro encontrado',
  draggable = false,
  onReorder = null
}) {
  const [sortColumn, setSortColumn] = useState(null)
  const [sortDirection, setSortDirection] = useState('asc')

  const handleSort = (column) => {
    if (!column.sortable) return
    
    if (sortColumn === column.key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column.key)
      setSortDirection('asc')
    }
  }

  const getSortIcon = (column) => {
    if (sortColumn !== column.key) return null
    return sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
  }

  // Ordenar dados baseado na coluna e direção selecionadas
  const sortedData = sortColumn ? [...data].sort((a, b) => {
    // Encontrar a coluna para verificar se tem função de ordenação customizada
    const column = columns.find(col => col.key === sortColumn)
    
    // Se a coluna tiver uma função de ordenação customizada, usar ela
    if (column && column.sortFn) {
      const comparison = column.sortFn(a, b)
      return sortDirection === 'asc' ? comparison : -comparison
    }

    let aValue = a[sortColumn]
    let bValue = b[sortColumn]

    // Tratar valores nulos/undefined - colocar no final
    if (aValue === null || aValue === undefined) {
      return sortDirection === 'asc' ? 1 : -1
    }
    if (bValue === null || bValue === undefined) {
      return sortDirection === 'asc' ? -1 : 1
    }

    // Tentar converter para Date se parecer uma data
    const aDate = new Date(aValue)
    const bDate = new Date(bValue)
    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime()) && 
        (String(aValue).includes('T') || String(aValue).includes('-') || String(aValue).includes('/'))) {
      const comparison = aDate.getTime() - bDate.getTime()
      return sortDirection === 'asc' ? comparison : -comparison
    }

    // Tentar converter para número se ambos forem numéricos
    const aNum = parseFloat(aValue)
    const bNum = parseFloat(bValue)
    if (!isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum)) {
      return sortDirection === 'asc' ? aNum - bNum : bNum - aNum
    }

    // Comparação de strings (case-insensitive)
    const aStr = String(aValue).toLowerCase()
    const bStr = String(bValue).toLowerCase()
    const comparison = aStr.localeCompare(bStr, 'pt-BR', { numeric: true, sensitivity: 'base' })
    return sortDirection === 'asc' ? comparison : -comparison
  }) : data

  if (loading) {
    return (
      <div className="data-table-loading">
        <div className="loading-spinner"></div>
        <p>Carregando dados...</p>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="data-table-empty">
        <p>{emptyMessage}</p>
      </div>
    )
  }

  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={column.sortable ? 'sortable' : ''}
                onClick={() => handleSort(column)}
                style={{ width: column.width }}
              >
                <div className="th-content">
                  <span>{column.label}</span>
                  {column.sortable && (
                    <span className="sort-icon">{getSortIcon(column)}</span>
                  )}
                </div>
              </th>
            ))}
            {actions.length > 0 && (
              <th className="actions-header">Ações</th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedData.filter(row => row && typeof row === 'object').map((row, index) => (
            <tr
              key={row.id || index}
              onClick={(e) => onRowClick && onRowClick(row, e)}
              className={`${onRowClick ? 'clickable' : ''} ${draggable ? 'draggable-row' : ''}`}
              draggable={draggable}
              onDragStart={(e) => {
                if (draggable) {
                  e.dataTransfer.effectAllowed = 'move'
                  e.dataTransfer.setData('text/plain', index.toString())
                  e.currentTarget.classList.add('dragging')
                }
              }}
              onDragEnd={(e) => {
                e.currentTarget.classList.remove('dragging')
              }}
              onDragOver={(e) => {
                if (draggable) {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  e.currentTarget.classList.add('drag-over')
                }
              }}
              onDragLeave={(e) => {
                e.currentTarget.classList.remove('drag-over')
              }}
              onDrop={(e) => {
                if (draggable && onReorder) {
                  e.preventDefault()
                  e.currentTarget.classList.remove('drag-over')
                  
                  const draggedIndex = parseInt(e.dataTransfer.getData('text/plain'))
                  // Encontrar o índice no array original (não ordenado)
                  const targetRow = sortedData[index]
                  const targetIndex = data.findIndex(item => item.id === targetRow.id)
                  
                  if (draggedIndex >= 0 && targetIndex >= 0 && draggedIndex !== targetIndex) {
                    // Usar os dados originais (não ordenados) para manter a ordem correta
                    const newData = [...data]
                    const [removed] = newData.splice(draggedIndex, 1)
                    newData.splice(targetIndex, 0, removed)
                    
                    // Extrair IDs na nova ordem
                    const orderedIds = newData.map(item => item.id)
                    onReorder(orderedIds, newData)
                  }
                }
              }}
            >
              {columns.map((column) => (
                <td key={column.key}>
                  {column.render
                    ? column.render(row[column.key], row)
                    : row[column.key]}
                </td>
              ))}
              {actions.length > 0 && (
                <td className="actions-cell">
                  <div className="actions-buttons">
                    {actions.map((action, actionIndex) => {
                      const IconComponent = action.icon
                      const isReactComponent = IconComponent && typeof IconComponent === 'function'
                      
                      return (
                        <button
                          key={actionIndex}
                          className={`action-button ${action.variant || 'default'}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            action.onClick(row)
                          }}
                          title={action.label}
                        >
                          {isReactComponent ? (
                            <IconComponent size={18} />
                          ) : typeof action.icon === 'string' ? (
                            action.icon
                          ) : (
                            action.label
                          )}
                        </button>
                      )
                    })}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {pagination && (
        <div className="data-table-pagination">
          <div className="pagination-info">
            Mostrando {pagination.from} a {pagination.to} de {pagination.total}
          </div>
          <div className="pagination-controls">
            <button
              disabled={!pagination.hasPrev}
              onClick={pagination.onPrev}
              className="pagination-button"
            >
              <ChevronLeft size={18} />
              Anterior
            </button>
            <span className="pagination-page">
              Página {pagination.current} de {pagination.totalPages}
            </span>
            <button
              disabled={!pagination.hasNext}
              onClick={pagination.onNext}
              className="pagination-button"
            >
              Próxima
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataTable

